import axios, { AxiosRequestConfig } from 'axios';
import pkceChallenge from 'pkce-challenge';

import { PATH_TO_AUTH_API, PATH_TO_AUTH_API_EXTERNAL } from '@shared/constants';
import { setAuthenticationState } from '@shared/util/localStorageAccessor';
import { getTenant } from '@shared/util/tenant';
import { UserClaims } from '@shared/util/user';

const defaultOpts: AxiosRequestConfig = {
  baseURL: PATH_TO_AUTH_API,
};

/**
 * Prepares data to send in application/x-www-form-urlencoded format.
 * See https://github.com/axios/axios#using-applicationx-www-form-urlencoded-format
 * @param payload the login data.
 */
const getAuthForm = (payload: object) => {
  const obj = {
    ...payload,
    grant_type: 'password',
    client_id: 'prisma-cloud-spa',
  };
  return new URLSearchParams(obj);
};

function getRedirectUri() {
  const baseUrl = window.location.origin;
  return new URL('', baseUrl).toString();
}

/**
 * Axios API to authenticate the user.
 */
export const TokenApi = {
  async externalLogin(provider: string) {
    const url = new URL(`${PATH_TO_AUTH_API_EXTERNAL}/connect/authorize`);
    const currentTenant = getTenant();
    if (currentTenant) {
      // Here we implement Authorization Code Flow with Proof Key for Code Exchange (PKCE)
      // See: https://auth0.com/docs/get-started/authentication-and-authorization-flow/authorization-code-flow-with-pkce
      const state = (await pkceChallenge(43)).code_verifier;
      const challenge = await pkceChallenge();
      setAuthenticationState(state, challenge.code_verifier);
      url.searchParams.append('client_id', 'prisma-cloud-spa');
      url.searchParams.append('redirect_uri', getRedirectUri());
      url.searchParams.append('response_type', 'code');
      url.searchParams.append('scope', 'offline_access');
      url.searchParams.append('state', state);
      url.searchParams.append('code_challenge', challenge.code_challenge);
      url.searchParams.append('code_challenge_method', 'S256');
      url.searchParams.append('response_mode', 'query');
      url.searchParams.append('identity_provider', provider);
      url.searchParams.append('tenant', currentTenant);
      window.location.href = url.toString();
    }
  },

  /**
   * Gets information about the currently authenticated user from the Auth service.
   * @param options Overrides HTTP request options.
   */
  async whoAmI(options?: AxiosRequestConfig): Promise<UserClaims> {
    const response = await axios.get<UserClaims>('/whoAmI', {
      withCredentials: true,
      ...defaultOpts,
      ...options,
    });

    return response.data;
  },

  /**
   * Completes the OAuth authorization code flow.
   * Exchanges the authorization code for tokens and the Auth service sets the JWT access token in httpOnly cookies.
   * @param code The authorization code received from the authorization endpoint.
   * @param codeVerifier The code verifier used in PKCE.
   * @param options Overrides HTTP request options.
   */
  async exchangeAuthorizationCode(code: string, codeVerifier: string, options?: AxiosRequestConfig) {
    const formData = {
      grant_type: 'authorization_code',
      redirect_uri: getRedirectUri(),
      code: code,
      code_verifier: codeVerifier ?? '',
      client_id: 'prisma-cloud-spa',
    };
    const form = new URLSearchParams(formData);
    options = { withCredentials: true, ...defaultOpts, ...options };
    await axios.post('/connect/token', form, options);
  },

  /**
   * Authenticates the user with the Auth service.
   * Sends credentials and the Auth service sets the JWT access token in httpOnly cookies.
   * @param userName The user email.
   * @param password The password.
   * @param options Overrides HTTP request options.
   */
  authenticate(userName: string, password: string, options?: AxiosRequestConfig): Promise<void> {
    const form = getAuthForm({ username: userName, password });
    options = { withCredentials: true, ...defaultOpts, ...options };
    return axios.post('/connect/token', form, options);
  },

  /**
   * Authenticates the user with two-factor authentication.
   * Sends credentials with verification code and the Auth service sets the JWT access token in httpOnly cookies.
   * @param userName The user email.
   * @param password The password.
   * @param verificationCode The second factor verification code.
   * @param trustedBrowser A boolean value indicating whether the current browser is trusted
   *      and should request the second factor less often.
   * @param options Overrides HTTP request options.
   */
  authenticateWithTwoFactor(
    userName: string,
    password: string,
    verificationCode: string,
    trustedBrowser: boolean,
    options?: AxiosRequestConfig,
  ): Promise<void> {
    const form = getAuthForm({ username: userName, password, verificationCode, trustedBrowser });
    options = { withCredentials: true, ...defaultOpts, ...options };
    return axios.post('/connect/token', form, options);
  },

  /**
   * Refreshes the user's authentication session.
   * Sends a refresh request and the Auth service sets a new JWT access token in httpOnly cookies.
   * @param options Overrides HTTP request options.
   * @returns 'token refreshed' if successful, 'token expired' if the refresh token is no longer valid.
   */
  async refreshAuthentication(options?: AxiosRequestConfig): Promise<'token refreshed' | 'token expired'> {
    const tenant = getTenant();
    if (!tenant) {
      throw Error('Cannot refresh access token. Tenant was null or undefined.');
    }

    const form = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: 'prisma-cloud-spa',
      tenant,
    });
    options = { withCredentials: true, ...defaultOpts, ...options };
    try {
      await axios.post('/connect/token', form, options);
      return 'token refreshed';
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        return 'token expired';
      }

      throw error;
    }
  },
};
